import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, ArrowLeft, Mail, Lock, User, Sparkles, Check, Camera, TrendingUp, MessageCircle, Calendar, Dumbbell, Gift, KeyRound, CheckCircle, HeartPulse } from 'lucide-react';
import SocialLoginButtons from './SocialLoginButtons';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = ({ mode = 'login', onAuthSuccess, onBack, onSwitchMode, resetToken = null }) => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(!!resetToken);
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
  const [currentResetToken, setCurrentResetToken] = useState(resetToken || '');
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState(null);

  // APP STORE COMPLIANCE (Apple Guideline 2.1a / 4.8):
  // Hide social login buttons on iOS by default (Apple rejected app 10x for broken Apple Sign-In).
  // Default = hidden. Only show on confirmed web/Android platforms.
  const [showSocialLogin, setShowSocialLogin] = useState(false);
  useEffect(() => {
    const isIOSNative =
      window.Capacitor?.isNativePlatform?.() && window.Capacitor?.getPlatform?.() === 'ios';
    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOSWebView = /iphone|ipad|ipod/.test(ua) && /applewebkit/.test(ua) && !/safari/.test(ua);
    if (!isIOSNative && !isIOSWebView) {
      setShowSocialLogin(true);
    }
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    referral_code: ''
  });

  // Check URL for referral code or reset token on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    const urlResetToken = urlParams.get('reset_token');
    
    if (refCode && !isLogin) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      validateReferralCode(refCode);
    }
    
    if (urlResetToken) {
      setCurrentResetToken(urlResetToken);
      setShowResetPassword(true);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isLogin]);

  const features = [
    { icon: Camera, text: 'AI Food Analysis', color: 'text-[#FF6600]' },
    { icon: TrendingUp, text: 'Progress Tracking', color: 'text-emerald-500' },
    { icon: MessageCircle, text: 'AI Nutritionist', color: 'text-blue-500' },
    { icon: Calendar, text: 'Meal Planning', color: 'text-purple-500' },
    { icon: Dumbbell, text: 'Exercise Coach', color: 'text-red-500' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError('');
    
    // Validate referral code when it changes
    if (name === 'referral_code' && value.length >= 10) {
      validateReferralCode(value);
    } else if (name === 'referral_code') {
      setReferralValid(null);
      setReferrerName('');
    }
  };

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/referral/validate/${code}`);
      setReferralValid(response.data.valid);
      setReferrerName(response.data.referrer_name || '');
    } catch (err) {
      setReferralValid(false);
      setReferrerName('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Confirm password match check (signup only)
    if (!isLogin && formData.password !== signupConfirmPassword) {
      setError('Passwords do not match. Please re-enter the same password in both fields.');
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      // iOS/iPadOS fix: normalize email (lowercase + trim) and trim password
      // to avoid auto-capitalize / autofill whitespace issues that caused
      // Apple App Review login failures.
      const cleanEmail = (formData.email || '').trim().toLowerCase();
      const cleanPassword = (formData.password || '').trim();
      const payload = isLogin 
        ? { email: cleanEmail, password: cleanPassword, remember_me: rememberMe }
        : { 
            name: (formData.name || '').trim(), 
            email: cleanEmail, 
            password: cleanPassword,
            referral_code: formData.referral_code || null,
            terms_accepted: true
          };
      
      // Robust retry logic for network AND server errors
      let response;
      let lastError;
      const maxRetries = 3;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          response = await axios.post(`${API}${endpoint}`, payload, { timeout: 30000 });
          break;
        } catch (retryErr) {
          lastError = retryErr;
          const status = retryErr.response?.status;
          const isServerError = status && status >= 500;
          const isNetworkError = !retryErr.response;
          // Retry on network errors or server 5xx errors
          if (attempt < maxRetries && (isNetworkError || isServerError)) {
            await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
            continue;
          }
          throw retryErr;
        }
      }
      
      if (!response) throw lastError;
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setPendingCredentials({
          email: formData.email,
          password: formData.password,
          remember_me: rememberMe
        });
        setShow2FAVerify(true);
        setLoading(false);
        return;
      }
      
      onAuthSuccess(response.data.token, response.data.user);
    } catch (err) {
      // Extract error detail from response
      let errorMsg;
      if (!err.response) {
        errorMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        const data = err.response.data;
        const status = err.response.status;
        // Handle JSON responses with detail field
        if (data && typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (data && Array.isArray(data.detail)) {
          errorMsg = data.detail.map(e => e.msg).join('. ');
        } else if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
          // Handle HTML responses (e.g., Cloudflare challenge pages)
          errorMsg = 'The server is temporarily busy. Please wait a moment and try again.';
        } else if (status === 401) {
          errorMsg = 'Invalid email or password. Please check your credentials.';
        } else if (status === 403) {
          errorMsg = 'Access temporarily restricted. Please try again in a moment.';
        } else if (status >= 500) {
          errorMsg = 'Server error. Please try again.';
        } else {
          errorMsg = isLogin ? 'Sign in failed. Please verify your email and password.' : 'Registration failed. Please try again.';
        }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6 && twoFactorCode.length !== 8) {
      setError('Please enter a valid code');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login/2fa`, {
        email: pendingCredentials.email,
        password: pendingCredentials.password,
        two_factor_code: twoFactorCode,
        remember_me: pendingCredentials.remember_me
      });
      
      onAuthSuccess(response.data.token, response.data.user);
    } catch (err) {
      const d2 = err.response?.data?.detail;
      setError(typeof d2 === 'string' ? d2 : 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setSuccessMessage('If an account with that email exists, a password reset link has been sent. Please check your inbox.');
    } catch (err) {
      const d3 = err.response?.data?.detail;
      setError(typeof d3 === 'string' ? d3 : 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: currentResetToken, 
        new_password: newPassword 
      });
      setSuccessMessage('Password reset successfully! You can now log in with your new password.');
      setShowResetPassword(false);
      setCurrentResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      // Switch to login view
      setIsLogin(true);
    } catch (err) {
      const d4 = err.response?.data?.detail;
      setError(typeof d4 === 'string' ? d4 : 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccessMessage('');
    setFormData({ name: '', email: '', password: '', referral_code: '' });
    setReferralValid(null);
    setReferrerName('');
    setRememberMe(false);
    setShowForgotPassword(false);
    if (onSwitchMode) onSwitchMode(!isLogin ? 'login' : 'register');
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-800">
        {/* Black Header */}
        <div className="bg-black py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setShowForgotPassword(false); setError(''); setSuccessMessage(''); }}
                className="text-white p-3 hover:bg-gray-800 rounded-lg transition-colors flex flex-col justify-center items-center gap-1.5"
                aria-label="Back"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
              <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
            </div>
            <button
              onClick={() => { setShowForgotPassword(false); setError(''); setSuccessMessage(''); }}
              className="flex items-center gap-2 text-white hover:text-[#FF6600] transition-colors font-medium text-sm"
            >
              <ArrowLeft size={18} />
              Back to Login
            </button>
          </div>
        </div>

        {/* Forgot Password Form */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#FF6600]/10 p-8 border border-white/50">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound size={32} className="text-[#FF6600]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Forgot Password?</h2>
                <p className="text-gray-500 dark:text-gray-400">Enter your email and we'll send you a link to reset your password.</p>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.emailAddress')}</label>
                  <div className="relative">
                    <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => { setForgotEmail(e.target.value); setError(''); setSuccessMessage(''); }}
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl text-green-600 text-sm font-medium flex items-start gap-2">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <span>{successMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Send Reset Link</>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => { setShowForgotPassword(false); setError(''); setSuccessMessage(''); }}
                  className="text-[#FF6600] font-semibold hover:text-orange-600 transition-colors"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset Password View (from email link)
  if (showResetPassword) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-800">
        {/* Black Header */}
        <div className="bg-black py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
            </div>
          </div>
        </div>

        {/* Reset Password Form */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#FF6600]/10 p-8 border border-white/50">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-[#FF6600]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Create New Password</h2>
                <p className="text-gray-500 dark:text-gray-400">Enter your new password below.</p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.newPassword')}</label>
                  <div className="relative">
                    <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.confirmNewPassword')}</label>
                  <div className="relative">
                    <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="p-4 bg-green-50 border border-green-100 rounded-xl text-green-600 text-sm font-medium flex items-start gap-2">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <span>{successMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Reset Password</>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Verification View
  if (show2FAVerify) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-gray-800">
        {/* Black Header */}
        <div className="bg-black py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setShow2FAVerify(false); setTwoFactorCode(''); setError(''); }}
                className="text-white p-3 hover:bg-gray-800 rounded-lg transition-colors flex flex-col justify-center items-center gap-1.5"
                aria-label="Back"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
              <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
            </div>
            <button
              onClick={() => { setShow2FAVerify(false); setTwoFactorCode(''); setError(''); }}
              className="flex items-center gap-2 text-white hover:text-[#FF6600] transition-colors font-medium text-sm"
            >
              <ArrowLeft size={18} />
              Back to Login
            </button>
          </div>
        </div>

        {/* 2FA Verification Form */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#FF6600]/10 p-8 border border-white/50">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} className="text-[#FF6600]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h2>
                <p className="text-gray-500 dark:text-gray-400">Enter the 6-digit code from your authenticator app</p>
              </div>

              <form onSubmit={handle2FAVerify} className="space-y-5">
                <div>
                  <input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 8).toUpperCase();
                      setTwoFactorCode(val);
                      setError('');
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-4 text-center text-3xl font-mono tracking-[0.5em] bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white"
                    maxLength={8}
                    autoFocus
                    data-testid="2fa-login-input"
                  />
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Or enter an 8-character backup code
                  </p>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (twoFactorCode.length !== 6 && twoFactorCode.length !== 8)}
                  className="w-full py-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
                  data-testid="2fa-login-submit"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-800">
      {/* Black Header Section - Consistent with all pages */}
      <div className="bg-black py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left Side - Back Button + Logo */}
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="text-white p-3 hover:bg-gray-800 rounded-lg transition-colors flex flex-col justify-center items-center gap-1.5"
                aria-label="Back to home"
                data-testid="back-to-landing-btn"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
            )}
            
            {/* Logo */}
            <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
          </div>
          
          {/* Right Side - Back to Home link */}
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white hover:text-[#FF6600] transition-colors font-medium text-sm"
            >
              <ArrowLeft size={18} />
              Back to Home
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        {/* Main Card */}
        <div className="w-full max-w-md relative z-10">
          {/* Auth Card */}
          <div className="bg-white dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#FF6600]/10 p-8 border border-white/50 animate-slide-up">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? t('auth.welcomeBack') : t('auth.createAccount')}
              </h2>
            <p className="text-gray-500 dark:text-gray-400">
              {isLogin 
                ? t('auth.loginSubtitle') 
                : t('auth.signupSubtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* APP STORE REVIEWER BANNER — Highly visible one-tap login.
                Apple/Google reviewers may skip the form and tap this directly. */}
            {isLogin && (
              <button
                type="button"
                data-testid="reviewer-demo-btn"
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    const res = await axios.post(`${API}/auth/login`, {
                      email: 'testexercise@test.com',
                      password: 'testexercise',
                      remember_me: false
                    }, { timeout: 30000 });
                    let data = res.data;
                    if (typeof data === 'string') {
                      try { data = JSON.parse(data); } catch(e) {
                        setError('Response parse error');
                        return;
                      }
                    }
                    if (data && data.token) {
                      onAuthSuccess(data.token, data.user);
                    } else {
                      setError('No token received');
                    }
                  } catch (err) {
                    setError('Demo login error: ' + (err.message || 'Unknown') + ' Status=' + (err.response?.status || 'none'));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-base rounded-xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 border-2 border-orange-300 animate-pulse-slow"
              >
                <Sparkles size={20} />
                <span>App Reviewer? Tap Here for Demo Access</span>
              </button>
            )}

            {!isLogin && (
              <div className="animate-fade-in">
                <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.fullName')}</label>
                <div className="relative">
                  <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.emailAddress')}</label>
              <div className="relative">
                <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                  spellCheck="false"
                  inputMode="email"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.password')}</label>
              <div className="relative">
                <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  spellCheck="false"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password — signup only */}
            {!isLogin && (
              <div className="animate-fade-in">
                <label className="block text-sm font-semibold text-orange-400 mb-2">{t('auth.confirmPassword')}</label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showSignupConfirmPassword ? 'text' : 'password'}
                    value={signupConfirmPassword}
                    onChange={(e) => { setSignupConfirmPassword(e.target.value); setError(''); }}
                    placeholder={t('auth.reenterPassword')}
                    className="w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl focus:outline-none focus:border-orange-400 focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium"
                    required
                    data-testid="signup-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-300 transition-colors"
                  >
                    {showSignupConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {signupConfirmPassword && formData.password && signupConfirmPassword !== formData.password && (
                  <p className="text-xs text-red-500 mt-1.5" data-testid="password-mismatch-warning">
                    Passwords do not match
                  </p>
                )}
              </div>
            )}

            {/* Remember Me - Only show for login */}
            {isLogin && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setRememberMe(!rememberMe)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      rememberMe 
                        ? 'bg-[#FF6600] border-[#FF6600]' 
                        : 'bg-white dark:bg-gray-800 border-gray-300 hover:border-[#FF6600]'
                    }`}
                    data-testid="remember-me-checkbox"
                  >
                    {rememberMe && <Check size={14} className="text-white" />}
                  </button>
                  <label 
                    onClick={() => setRememberMe(!rememberMe)}
                    className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none"
                  >
                    Remember me for 90 days
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setError(''); }}
                  className="text-sm text-[#FF6600] hover:text-orange-600 font-medium transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {/* Referral Code - Only show for registration */}
            {!isLogin && (
              <div className="animate-fade-in">
                <label className="block text-sm font-semibold text-orange-400 mb-2">
                  {t('auth.referralCode')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
                </label>
                <div className="relative">
                  <Gift size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="referral_code"
                    value={formData.referral_code}
                    onChange={handleInputChange}
                    placeholder="NUTRI-FRIEND-XXXX"
                    className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 dark:bg-gray-900 border-2 rounded-xl focus:outline-none focus:bg-white dark:focus:bg-gray-800 transition-all text-gray-900 dark:text-white font-medium uppercase ${
                      referralValid === true 
                        ? 'border-green-400 focus:border-green-400' 
                        : referralValid === false 
                        ? 'border-red-300 focus:border-red-400' 
                        : 'border-gray-100 dark:border-gray-700 focus:border-orange-400'
                    }`}
                    data-testid="referral-input"
                  />
                  {referralValid === true && (
                    <Check size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" />
                  )}
                </div>
                {referralValid === true && referrerName && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <Check size={14} />
                    Referred by {referrerName} - You'll get 1 week free Premium!
                  </p>
                )}
                {referralValid === false && formData.referral_code && (
                  <p className="text-sm text-red-500 mt-2">Invalid referral code</p>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm font-medium animate-shake">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? t('authPage.signIn') : t('authPage.signUp')}
                  <Sparkles size={20} />
                </>
              )}
            </button>

            {/* Demo Mode Button - For App Review */}
            {isLogin && (
              <button
                type="button"
                data-testid="demo-mode-btn"
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    const res = await axios.post(`${API}/auth/login`, {
                      email: 'testexercise@test.com',
                      password: 'testexercise',
                      remember_me: false
                    }, { timeout: 30000 });
                    let data = res.data;
                    if (typeof data === 'string') {
                      try { data = JSON.parse(data); } catch(e) {
                        setError('Response parse error');
                        return;
                      }
                    }
                    if (data && data.token) {
                      onAuthSuccess(data.token, data.user);
                    } else {
                      setError('Debug: No token. Type=' + typeof data + ' Keys=' + Object.keys(data || {}).join(','));
                    }
                  } catch (err) {
                    setError('Demo error: ' + (err.message || 'Unknown') + ' Status=' + (err.response?.status || 'none'));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold text-sm rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600"
              >
                <HeartPulse size={16} className="text-[#FF6600]" />
                {t('auth.tryDemo')}
              </button>
            )}

            {/* Implicit Terms Consent - Only for registration */}
            {!isLogin && (
              <p className="text-xs text-center text-gray-500 dark:text-gray-400 leading-relaxed" data-testid="implicit-terms-notice">
                {t('auth.implicitTermsPrefix')}{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); if (onSwitchMode) onSwitchMode('terms-of-service'); }} className="text-[#FF6600] font-semibold hover:underline">{t('auth.termsOfService')}</a>
                {' '}{t('auth.and')}{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); if (onSwitchMode) onSwitchMode('privacy-policy'); }} className="text-[#FF6600] font-semibold hover:underline">{t('auth.privacyPolicy')}</a>.
                {' '}{t('auth.medicalDisclaimer')}
              </p>
            )}
          </form>

          {/* Social Login (hidden on iOS for App Store compliance) */}
          {showSocialLogin && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">{t('authPage.orContinueWith')}</span>
                </div>
              </div>

              <SocialLoginButtons 
                onSuccess={onAuthSuccess}
                onError={(err) => setError(err)}
                mode={isLogin ? 'login' : 'register'}
              />
            </>
          )}

          {/* Switch Mode */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {isLogin ? t('authPage.noAccount') : t('authPage.hasAccount')}
              <button
                onClick={toggleMode}
                className="ml-2 text-[#FF6600] font-semibold hover:text-orange-600 transition-colors"
                data-testid="switch-mode-btn"
              >
                {isLogin ? t('authPage.signUp') : t('authPage.signIn')}
              </button>
            </p>
          </div>

          {/* Features Preview */}
          <div className="mt-8 pt-8 border-t border-gray-100 dark:border-gray-700">
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">What you'll get access to</p>
            <div className="flex flex-wrap justify-center gap-3">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300"
                >
                  <feature.icon size={14} className={feature.color} />
                  {feature.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center animate-fade-in-delayed">
          <div className="bg-white dark:bg-gray-800/60 backdrop-blur rounded-2xl p-4">
            <div className="text-2xl font-bold text-[#FF6600]">200M+</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Meals Tracked</div>
          </div>
          <div className="bg-white dark:bg-gray-800/60 backdrop-blur rounded-2xl p-4">
            <div className="text-2xl font-bold text-[#FF6600]">50K+</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Active Users</div>
          </div>
          <div className="bg-white dark:bg-gray-800/60 backdrop-blur rounded-2xl p-4">
            <div className="text-2xl font-bold text-[#FF6600]">4.8★</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Rating</div>
          </div>
        </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }
        .animate-shake {
          animation: shake 0.3s ease-out;
        }
        .animate-fade-in-delayed {
          animation: fade-in 0.5s ease-out;
          animation-delay: 0.3s;
          animation-fill-mode: both;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
